import { normalizeSportName } from './drillTemplates';

export const SAS_ANALYSIS_SCHEMA_VERSION = 1;
export const CORE_ANALYSIS_FRAMEWORK_ID = 'core_movement_analysis_v1';
export const SAS_MOVEMENT_ANALYSIS_FRAMEWORK_ID = 'sas_movement_analysis_v1';
export const SAS_SNOWBOARD_ANALYSIS_VARIANT = 'sas_snowboard_rider_analysis_v1';
export const SAS_SKI_ANALYSIS_VARIANT = 'sas_ski_analysis_v1';

export const ANALYSIS_FRAMEWORK = {
  id: SAS_MOVEMENT_ANALYSIS_FRAMEWORK_ID,
  name: 'SAS Movement Analysis Framework',
};

const SNOWBOARD_INEFFICIENCY_OPTIONS = Object.freeze([
  { value: 'edge_preference', label: 'Edge preference / one edge feels safer' },
  { value: 'loss_of_balance', label: 'Loss of balance' },
  { value: 'hand_touch', label: 'Hand touch / touchdown' },
  { value: 'jerky_upper_body', label: 'Jerky upper-body movement' },
  { value: 'late_transition', label: 'Late transition' },
  { value: 'open_turns', label: 'Open turns' },
  { value: 'inconsistent_track', label: 'Inconsistent track' },
  { value: 'counter_rotation', label: 'Counter-rotation' },
  { value: 'excessive_lateral', label: 'Excessive lateral movement' },
  { value: 'aft_stance', label: 'Aft stance' },
]);

const SNOWBOARD_BOARD_PERFORMANCE_OPTIONS = Object.freeze([
  { value: 'skid', label: 'Skid' },
  { value: 'chatter', label: 'Chatter' },
  { value: 'loss_of_momentum', label: 'Loss of momentum' },
  { value: 'open_turns', label: 'Open turns' },
  { value: 'inconsistent_track', label: 'Inconsistent track' },
  { value: 'speed_control_issue', label: 'Speed-control issue' },
  { value: 'late_edge', label: 'Late edge' },
  { value: 'washed_tail', label: 'Washed tail' },
  { value: 'stalled_board', label: 'Stalled board' },
  { value: 'abrupt_edge_change', label: 'Abrupt edge change' },
]);

const SNOWBOARD_TURN_SIDE_OPTIONS = Object.freeze([
  { value: 'heel', label: 'Heel-side' },
  { value: 'toe', label: 'Toe-side' },
  { value: 'both', label: 'Both' },
]);

const SNOWBOARD_TURN_PHASE_OPTIONS = Object.freeze([
  { value: 'initiation', label: 'Initiation' },
  { value: 'control', label: 'Control' },
  { value: 'completion', label: 'Completion' },
]);

const SKI_PRIMARY_PERFORMANCE_OPTIONS = Object.freeze([
  { value: 'late_edge_engagement', label: 'Late edge engagement' },
  { value: 'weak_outside_ski', label: 'Weak outside ski' },
  { value: 'inconsistent_turn_shape', label: 'Inconsistent turn shape' },
  { value: 'speed_control_issue', label: 'Speed-control issue' },
  { value: 'skidding', label: 'Skidding' },
  { value: 'lack_of_flow', label: 'Lack of flow' },
  { value: 'late_pressure_transfer', label: 'Late pressure transfer' },
  { value: 'unfinished_turn', label: 'Unfinished turn' },
]);

const SKI_PERFORMANCE_OPTIONS = Object.freeze([
  { value: 'skidding', label: 'Skidding' },
  { value: 'washed_out_finish', label: 'Washed-out finish' },
  { value: 'late_edge_set', label: 'Late edge set' },
  { value: 'weak_platform', label: 'Weak platform' },
  { value: 'inconsistent_radius', label: 'Inconsistent turn radius' },
  { value: 'speed_runaway', label: 'Runaway speed' },
  { value: 'limited_grip', label: 'Limited grip' },
  { value: 'inside_ski_dominance', label: 'Inside-ski dominance' },
]);

const SKI_TURN_PHASE_OPTIONS = Object.freeze([
  { value: 'initiation', label: 'Initiation' },
  { value: 'shaping', label: 'Shaping' },
  { value: 'finish', label: 'Finish' },
]);

const BASE_ANALYSIS_SECTIONS = Object.freeze({
  detect: Object.freeze({}),
  cause: Object.freeze({}),
  effect: Object.freeze({}),
  correct: Object.freeze({}),
  train: Object.freeze({}),
});

export const ANALYSIS_VARIANTS = Object.freeze({
  [SAS_SNOWBOARD_ANALYSIS_VARIANT]: Object.freeze({
    id: SAS_SNOWBOARD_ANALYSIS_VARIANT,
    name: 'SAS Snowboard Rider Analysis',
    sport: 'snowboarding',
    display: {
      editorTitle: 'Snowboard Rider Analysis',
      editorDescription: 'Use the Detect → Cause → Effect → Correct → Train workflow to turn the ride into a structured movement analysis. This variant is snowboard-specific.',
      saveLabel: 'Save Rider Analysis',
      savingLabel: 'Saving rider analysis...',
      studentTitle: 'Rider Analysis',
      studentDescription: 'Structured snowboard analysis from your coach using Detect → Cause → Effect → Correct → Train.',
      studentSections: {
        detect: 'What Your Coach Saw',
        cause: 'Why It\'s Happening',
        effect: 'What It Causes',
        correct: 'Correction Plan',
        train: 'Training Focus',
      },
      studentFields: {
        detect: {
          inlineFields: ['turnSide', 'turnPhase'],
          textFields: ['bigPictureIssue', 'edgePressureSteerObservation'],
          chipField: { key: 'observedInefficiencies', labelType: 'inefficiency' },
        },
        cause: {
          showStanceIssues: true,
          textFields: ['movementDescription'],
        },
        effect: {
          textFields: ['effectDescription'],
          chipField: { key: 'boardPerformanceIssues', labelType: 'boardPerformance' },
        },
        correct: {
          textFields: ['stanceCorrectionCue', 'movementCorrectionCue', 'feelOrAnalogy', 'terrainOrTask', 'mileageGoal'],
        },
        train: {
          textFields: ['notes'],
        },
      },
      stanceIssueFields: ['ankles', 'knees', 'hips', 'upperBody', 'foreAft', 'lateral', 'vertical', 'rotational'],
      editorFields: {
        detectSelects: [
          { key: 'turnSide', label: 'Turn Side', placeholder: 'Select turn side', options: SNOWBOARD_TURN_SIDE_OPTIONS },
          { key: 'turnPhase', label: 'Turn Phase', placeholder: 'Select turn phase', options: SNOWBOARD_TURN_PHASE_OPTIONS },
        ],
        detectTextareas: [
          { key: 'bigPictureIssue', placeholder: 'Big-picture issue or tendency you noticed', rows: 3 },
          { key: 'edgePressureSteerObservation', placeholder: 'Optional edge / pressure / steer observation', rows: 3 },
        ],
        causeMovement: { key: 'movementDescription', placeholder: 'Describe the inefficient stance and movement objectively', rows: 3 },
        correctTextareas: [
          { key: 'stanceCorrectionCue', label: 'Stance correction cue', rows: 3 },
          { key: 'movementCorrectionCue', label: 'Movement correction cue', rows: 3 },
          { key: 'feelOrAnalogy', label: 'Feel or analogy', rows: 3 },
          { key: 'terrainOrTask', label: 'Terrain or task', rows: 3 },
          { key: 'mileageGoal', label: 'Mileage goal', rows: 2 },
        ],
        trainTextareas: [
          { key: 'notes', placeholder: 'Optional training notes or progression reminders', rows: 3 },
        ],
      },
    },
    options: {
      inefficiency: SNOWBOARD_INEFFICIENCY_OPTIONS,
      boardPerformance: SNOWBOARD_BOARD_PERFORMANCE_OPTIONS,
    },
    sections: {
      ...BASE_ANALYSIS_SECTIONS,
      detect: {
        turnSide: '',
        turnPhase: '',
        bigPictureIssue: '',
        observedInefficiencies: [],
        edgePressureSteerObservation: '',
      },
      cause: {
        stanceIssues: {
          ankles: '',
          knees: '',
          hips: '',
          upperBody: '',
          foreAft: '',
          lateral: '',
          vertical: '',
          rotational: '',
        },
        movementDescription: '',
      },
      effect: {
        boardPerformanceIssues: [],
        effectDescription: '',
      },
      correct: {
        stanceCorrectionCue: '',
        movementCorrectionCue: '',
        feelOrAnalogy: '',
        terrainOrTask: '',
        mileageGoal: '',
      },
      train: {
        notes: '',
      },
    },
  }),
  [SAS_SKI_ANALYSIS_VARIANT]: Object.freeze({
    id: SAS_SKI_ANALYSIS_VARIANT,
    name: 'SAS Ski Analysis',
    sport: 'skiing',
    display: {
      editorTitle: 'Ski Analysis',
      editorDescription: 'Use the shared Detect → Cause → Effect → Correct → Train workflow in a ski-specific way: overall impression, ski performance, body movements, ideal comparison, and lesson plan.',
      saveLabel: 'Save Ski Analysis',
      savingLabel: 'Saving ski analysis...',
      studentTitle: 'Ski Analysis',
      studentDescription: 'Structured ski analysis from your coach using Detect → Cause → Effect → Correct → Train.',
      studentSections: {
        detect: 'Overall Impression',
        cause: 'Movement Cause',
        effect: 'Ski Performance',
        correct: 'Correction Plan',
        train: 'Training Focus',
      },
      studentFields: {
        detect: {
          inlineFields: ['terrainComfort', 'turnType', 'turnShape', 'turnPhase'],
          textFields: ['overallImpression'],
          chipField: { key: 'primaryPerformanceIssues', labelType: 'primary' },
        },
        cause: {
          showStanceIssues: true,
          textFields: ['movementDescription'],
        },
        effect: {
          textFields: ['effectDescription'],
          chipField: { key: 'skiPerformanceIssues', labelType: 'performance' },
        },
        correct: {
          textFields: ['compareToIdeal', 'developmentGoal', 'stanceCorrectionCue', 'movementCorrectionCue', 'feelOrAnalogy'],
        },
        train: {
          textFields: ['terrainOrTask', 'stepByStepPlan', 'mileageGoal'],
        },
      },
      stanceIssueFields: ['ankles', 'knees', 'hips', 'upperBody', 'foreAft', 'lateralBalance', 'rotaryInput', 'pressureManagement'],
      editorFields: {
        detectInputs: [
          { key: 'overallImpression', placeholder: 'Overall impression of the skier' },
          { key: 'terrainComfort', placeholder: 'Comfort on the terrain' },
          { key: 'turnType', placeholder: 'Turn type' },
          { key: 'turnShape', placeholder: 'Turn shape' },
        ],
        detectSelects: [
          { key: 'turnPhase', label: 'Turn Phase', placeholder: 'Select turn phase', options: SKI_TURN_PHASE_OPTIONS },
        ],
        causeMovement: { key: 'movementDescription', placeholder: 'Describe the key body movements causing the ski performance', rows: 3 },
        correctTextareas: [
          { key: 'compareToIdeal', label: 'Compare to the ideal', rows: 3 },
          { key: 'developmentGoal', label: 'Development goal', rows: 2 },
          { key: 'stanceCorrectionCue', label: 'Stance correction cue', rows: 3 },
          { key: 'movementCorrectionCue', label: 'Movement correction cue', rows: 3 },
          { key: 'feelOrAnalogy', label: 'Feel or analogy', rows: 3 },
        ],
        trainTextareas: [
          { key: 'terrainOrTask', label: 'Terrain or task', rows: 3 },
          { key: 'stepByStepPlan', label: 'Step-by-step lesson plan', rows: 3 },
          { key: 'mileageGoal', label: 'Mileage goal', rows: 2 },
        ],
      },
    },
    options: {
      primaryPerformance: SKI_PRIMARY_PERFORMANCE_OPTIONS,
      performance: SKI_PERFORMANCE_OPTIONS,
    },
    sections: {
      ...BASE_ANALYSIS_SECTIONS,
      detect: {
        overallImpression: '',
        terrainComfort: '',
        turnType: '',
        turnShape: '',
        turnPhase: '',
        primaryPerformanceIssues: [],
      },
      cause: {
        stanceIssues: {
          ankles: '',
          knees: '',
          hips: '',
          upperBody: '',
          foreAft: '',
          lateralBalance: '',
          rotaryInput: '',
          pressureManagement: '',
        },
        movementDescription: '',
      },
      effect: {
        skiPerformanceIssues: [],
        effectDescription: '',
      },
      correct: {
        compareToIdeal: '',
        developmentGoal: '',
        stanceCorrectionCue: '',
        movementCorrectionCue: '',
        feelOrAnalogy: '',
      },
      train: {
        terrainOrTask: '',
        stepByStepPlan: '',
        mileageGoal: '',
      },
    },
  }),
});

function cloneSection(section = {}) {
  return JSON.parse(JSON.stringify(section));
}

export function getAnalysisVariantDefinition(variantId) {
  return ANALYSIS_VARIANTS[variantId] || null;
}

export function getAnalysisDisplayMeta(variantId) {
  return getAnalysisVariantDefinition(variantId)?.display || null;
}

export function getVariantOptions(variantId, optionType) {
  const variant = getAnalysisVariantDefinition(variantId);
  if (!variant?.options?.[optionType]) return [];
  return variant.options[optionType];
}

export function getVariantOptionLabel(variantId, optionType, value) {
  const match = getVariantOptions(variantId, optionType).find((item) => item.value === value);
  return match?.label || String(value || '').replace(/_/g, ' ');
}

export function getAnalysisVariantForSport(videoOrSport, explicitVariant) {
  if (explicitVariant && ANALYSIS_VARIANTS[explicitVariant]) {
    return ANALYSIS_VARIANTS[explicitVariant];
  }

  const sport = normalizeSportName(typeof videoOrSport === 'string' ? videoOrSport : videoOrSport?.sport);
  if (sport === 'snowboarding') return ANALYSIS_VARIANTS[SAS_SNOWBOARD_ANALYSIS_VARIANT];
  if (sport === 'skiing') return ANALYSIS_VARIANTS[SAS_SKI_ANALYSIS_VARIANT];
  return null;
}

export function createEmptyAnalysisForVariant(variantId) {
  const variant = getAnalysisVariantDefinition(variantId);
  if (!variant) return null;

  return {
    framework: CORE_ANALYSIS_FRAMEWORK_ID,
    sport: variant.sport,
    variant: variant.id,
    detect: cloneSection(variant.sections.detect),
    cause: cloneSection(variant.sections.cause),
    effect: cloneSection(variant.sections.effect),
    correct: cloneSection(variant.sections.correct),
    train: cloneSection(variant.sections.train),
  };
}

export function createCanonicalAnalysisMeta(variantId) {
  const variant = getAnalysisVariantDefinition(variantId);
  if (!variant) return null;

  return {
    schemaVersion: SAS_ANALYSIS_SCHEMA_VERSION,
    framework: { ...ANALYSIS_FRAMEWORK },
    sport: variant.sport,
    variant: {
      id: variant.id,
      name: variant.name,
    },
  };
}
